// Admin-only Dashboard-Vorlagen settings page.
//
// Two modes:
// - List view: all templates with name, description, # users assigned,
//   actions (edit, delete, assign).
// - Builder view: a DashboardGrid configured for a single template — drag,
//   resize, add widgets just like a normal dashboard, but the layout is
//   persisted to the template via PATCH instead of the user's own layout.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { ArrowLeft, LayoutDashboard, Pencil, Plus, Trash2, Users } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  DashboardGrid,
  EMPTY_LAYOUT,
  type PersistedLayout,
} from '../components/dashboard/dashboard-grid'
import { SettingsLayout } from '../components/settings-layout'
import { MobileBottomSlots } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Dialog } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { api, type DashboardTemplate } from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/dashboard-templates',
  component: DashboardTemplatesPage,
})

function isPersistedLayout(v: unknown): v is PersistedLayout {
  return (
    !!v &&
    typeof v === 'object' &&
    (v as PersistedLayout).version === 1 &&
    Array.isArray((v as PersistedLayout).widgets)
  )
}

function DashboardTemplatesPage() {
  const qc = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data: tmplData, isLoading } = useQuery({
    queryKey: ['dashboard-templates'],
    queryFn: () => api.listDashboardTemplates(),
    enabled: meData?.user?.role === 'admin',
  })

  const [editingId, setEditingId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [assignFor, setAssignFor] = useState<DashboardTemplate | null>(null)

  const createMutation = useMutation({
    mutationFn: (body: { name: string; description?: string; layout: unknown }) =>
      api.createDashboardTemplate(body),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['dashboard-templates'] })
      setCreateOpen(false)
      setEditingId(r.template.id)
      notify.success('Vorlage erstellt')
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteDashboardTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard-templates'] })
      notify.success('Vorlage gelöscht')
    },
    onError: (e: Error) => notify.error(e.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (meData.user.role !== 'admin') return <Navigate to="/" />

  const templates = tmplData?.templates ?? []
  const editing = editingId ? templates.find((t) => t.id === editingId) : null

  // === Builder mode ===
  if (editing) {
    return (
      <SettingsLayout
        title="Vorlage bearbeiten"
        description={`Layout für »${editing.name}«. Änderungen werden automatisch gespeichert.`}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
              <ArrowLeft className="h-4 w-4" /> Zur Übersicht
            </Button>
            <Button size="sm" onClick={() => setAssignFor(editing)}>
              <Users className="h-4 w-4" /> Mitarbeitern zuweisen
            </Button>
          </div>
          <TemplateEditor template={editing} />
        </div>
        {assignFor && <AssignDialog template={assignFor} onClose={() => setAssignFor(null)} />}
      </SettingsLayout>
    )
  }

  // === List mode ===
  return (
    <SettingsLayout
      title="Dashboard-Vorlagen"
      description="Vorkonfigurierte Dashboards für Mitarbeiter. Zuweisen kopiert das Layout — Mitarbeiter können danach frei anpassen."
    >
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Neue Vorlage in der Bottom-Bar — derselbe
          Handler wie der Kopf-Button (nur Listen-Modus, der Builder hat
          seine Aktionen im Content). */}
      <MobileBottomSlots>
        <BarAccentPlus ariaLabel="Neue Vorlage" onClick={() => setCreateOpen(true)} />
      </MobileBottomSlots>

      <div className="space-y-4">
        {/* <640 übernimmt die Bottom-Bar — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
        <div className="flex phone:hidden justify-end">
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Neue Vorlage
          </Button>
        </div>

        {isLoading ? (
          <p className="text-text-secondary">Lade …</p>
        ) : templates.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
            <LayoutDashboard className="h-8 w-8 text-text-tertiary mx-auto mb-3" />
            <p className="text-text-secondary">
              Noch keine Vorlagen. Klicke »Neue Vorlage« um zu beginnen.
            </p>
          </div>
        ) : (
          <ul className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated/40">
            {templates.map((t, idx) => (
              <li
                key={t.id}
                className={`grid grid-cols-[1fr_auto_auto] items-center gap-4 px-5 py-3.5 ${idx < templates.length - 1 ? 'border-b border-border-subtle' : ''}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{t.name}</p>
                  {t.description && (
                    <p className="text-xs text-text-tertiary truncate">{t.description}</p>
                  )}
                  <p className="mt-1 text-2xs uppercase tracking-wider text-text-tertiary">
                    {t.assignedCount ?? 0} Mitarbeiter zugewiesen
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setAssignFor(t)}>
                  <Users className="h-4 w-4" /> Zuweisen
                </Button>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingId(t.id)}
                    aria-label="Bearbeiten"
                    className="text-text-tertiary hover:text-accent"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Vorlage »${t.name}« löschen?`,
                        description:
                          'Mitarbeiter, die nur diese Vorlage zugewiesen haben, behalten ihr aktuelles Layout.',
                        variant: 'danger',
                        confirmLabel: 'Löschen',
                      })
                      if (ok) deleteMutation.mutate(t.id)
                    }}
                    aria-label="Löschen"
                    className="text-text-tertiary hover:text-danger"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {createOpen && (
        <CreateDialog
          onClose={() => setCreateOpen(false)}
          onCreate={(name, description) =>
            createMutation.mutate({ name, description, layout: EMPTY_LAYOUT })
          }
          isPending={createMutation.isPending}
        />
      )}
      {assignFor && <AssignDialog template={assignFor} onClose={() => setAssignFor(null)} />}
    </SettingsLayout>
  )
}

// === Builder for a single template ===
function TemplateEditor({ template }: { template: DashboardTemplate }) {
  const qc = useQueryClient()
  const [local, setLocal] = useState<PersistedLayout>(() =>
    isPersistedLayout(template.layout) ? template.layout : EMPTY_LAYOUT,
  )
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const saveMutation = useMutation({
    mutationFn: (layout: PersistedLayout) => api.updateDashboardTemplate(template.id, { layout }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard-templates'] })
    },
    onError: (e: Error) => notify.error(e.message),
  })

  function handleChange(next: PersistedLayout) {
    setLocal(next)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => saveMutation.mutate(next), 400)
  }

  return <DashboardGrid value={local} onChange={handleChange} />
}

// === Create dialog (just name + description; layout starts empty) ===
function CreateDialog({
  onClose,
  onCreate,
  isPending,
}: {
  onClose: () => void
  onCreate: (name: string, description?: string) => void
  isPending: boolean
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  return (
    <Dialog
      open={true}
      onClose={onClose}
      title="Neue Dashboard-Vorlage"
      description="Du legst zuerst Name + Beschreibung an, anschließend baust du im Editor das Layout auf."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => onCreate(name.trim(), description.trim() || undefined)}
            disabled={!name.trim() || isPending}
          >
            <Plus className="h-4 w-4" /> Anlegen
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="tpl-name">Name</Label>
          <Input
            id="tpl-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Standard-Mitarbeiter"
            autoFocus
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="tpl-desc">Beschreibung (optional)</Label>
          <Input
            id="tpl-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Wofür ist die Vorlage gedacht?"
          />
        </div>
      </div>
    </Dialog>
  )
}

// === Assign dialog: pick users (multi-select), choose to push layout or
// just link the template. ===
function AssignDialog({ template, onClose }: { template: DashboardTemplate; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: usersData } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => api.listUsers(false),
  })
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [pushLayout, setPushLayout] = useState(true)

  const assignMutation = useMutation({
    mutationFn: () => api.assignDashboardTemplate(template.id, Array.from(selected), pushLayout),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['dashboard-templates'] })
      notify.success(
        `${r.count} ${r.count === 1 ? 'Mitarbeiter zugewiesen' : 'Mitarbeitern zugewiesen'}${r.pushed ? ' (Layout aufgepresst)' : ''}`,
      )
      onClose()
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const allUsers = (usersData?.users ?? []).filter((u) => !u.archived)

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      title={`Vorlage »${template.name}« zuweisen`}
      description="Wähle die Mitarbeiter aus, die diese Vorlage erhalten sollen."
      footer={
        <div className="flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={pushLayout}
              onChange={(e) => setPushLayout(e.target.checked)}
              className="accent-accent h-4 w-4"
            />
            Layout sofort übernehmen (überschreibt aktuelles Dashboard der Mitarbeiter)
          </label>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              onClick={() => assignMutation.mutate()}
              disabled={selected.size === 0 || assignMutation.isPending}
            >
              <Users className="h-4 w-4" /> Zuweisen ({selected.size})
            </Button>
          </div>
        </div>
      }
    >
      {allUsers.length === 0 ? (
        <p className="text-text-secondary text-sm py-4">Keine Mitarbeiter vorhanden.</p>
      ) : (
        <ul className="max-h-80 overflow-y-auto divide-y divide-border-subtle border border-border-subtle rounded-md">
          {allUsers.map((u) => (
            <li key={u.id}>
              <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-bg-overlay/40">
                <input
                  type="checkbox"
                  checked={selected.has(u.id)}
                  onChange={() => toggle(u.id)}
                  className="accent-accent h-4 w-4"
                />
                <span className="text-sm text-text-primary flex-1 truncate">{u.name}</span>
                <span className="text-xs text-text-tertiary">{u.email}</span>
                {u.role === 'admin' && (
                  <span className="text-2xs uppercase tracking-wider text-accent">admin</span>
                )}
              </label>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  )
}

// Use the auto-fixed export to avoid unused-import warnings if useEffect not used
void useEffect
